import { AutocompleteValue } from 'vetrf-ui/dist/components/form/fields/field/input/AutocompleteField/AutocompleteField';
import { ChangeParams } from 'utils/hooks/usePaginationRow';
import { EntryType, FindFnParams, ProductType } from 'components/registry/types/api';
import { baseApi } from 'api/base';
import {
  AnimalSpeciesDictOption,
  BaseDictCodes,
  BaseDictOption,
  ConsiderationPeriodDictOption,
  ConsignmentOriginDictOption,
  CountryDictOption,
  DocumentDictOption,
  MeasureDictOption,
  MeasureUnitsCodes,
  ObjectOperation,
  ObjectOperationParams,
  ObjectStatus,
  ObjectStatusParams,
  OrgLegalFormDTO,
  RegionDictOption,
  RegistryOperationDictOption,
  StatusesDictOption,
} from './params';

const baseDict = async <T = BaseDictOption>(
  code: BaseDictCodes,
  params?: Record<string, unknown>
): Promise<T[]> => {
  const res = await baseApi.get('/base/dictionary', { params: { ...params, code } });
  return res.data;
};

const getRequestsStatus = async (
  params: { excludeIds: string } | undefined = undefined
): Promise<StatusesDictOption[]> => {
  const res = await baseApi.get('/statuses/feedAdditiveRequest', {
    params,
  });
  return res.data;
};

const getRegistryStatus = async (): Promise<StatusesDictOption[]> => {
  const res = await baseApi.get('/statuses/feedAdditiveRegistry');
  return res.data;
};

const getStatuses = async (
  entryType: EntryType,
  productType: ProductType
): Promise<StatusesDictOption[]> => {
  const res = await baseApi.get(`/statuses/${entryType}/${productType}`);
  return res.data;
};

const getRegistryOperations = async (
  productType: ProductType,
  entryType?: EntryType
): Promise<RegistryOperationDictOption[]> => {
  const res = await baseApi.get(`/catalogs/registry-operations`, {
    params: { productType, registryEntryType: entryType },
  });

  return res.data.content;
};

const getCountries = async (
  isRussia: boolean | undefined = undefined
): Promise<CountryDictOption[]> => {
  const res = await baseApi.get('/refs/countries', {
    params: {
      isRussia,
    },
  });
  return res.data.content;
};

const getRussianRegions = async (): Promise<RegionDictOption[]> => {
  const res = await baseApi.get('/refs/russian-regions');
  return res.data.content;
};

const getDistricts = async (id: string): Promise<RegionDictOption[]> => {
  const res = await baseApi.get(`/refs/district/${id}/localityList.json`);
  return res.data;
};

const getDocumentTypes = async (): Promise<DocumentDictOption[]> => {
  const res = await baseApi.get('/refs/documentTypes.json');
  return res.data;
};

const getConsiderationPeriodTypes = async (): Promise<ConsiderationPeriodDictOption[]> => {
  const res = await baseApi.get('/catalogs/consideration-period-types');
  return res.data.content;
};

const getAnimalTypes = async (): Promise<AnimalSpeciesDictOption[]> => {
  const res = await baseApi.get('/catalogs/animal-species?sort=name,asc');
  return res.data.content;
};

const getSigningPerson = async (): Promise<AnimalSpeciesDictOption[]> => {
  const res = await baseApi.get('/catalogs/signing-person');
  return res.data.content;
};

const getPharmProductsList = async (name: string | undefined) => {
  const res = await baseApi.post('/pharm/list', {
    productType: ProductType.pharm,
    entryType: EntryType.registry,
    extendedQuery: {
      name,
    },
  });

  return res.data.result;
};

const getGMO = async (params: ChangeParams) => {
  const res = await baseApi.post('/gmo/list', {
    pageDetails: {
      itemsPerPage: params.pagination.count,
      pageNumber: params.pagination.page,
    },
    productType: ProductType.gmo,
    entryType: 'product',
    sortOrders: [],
    extendedQuery: {},
  });

  return {
    totalCount: res.data.total,
    data: res.data.result,
  };
};

const getPharm = async (params: ChangeParams) => {
  const res = await baseApi.post('/list/pagedList', {
    pageDetails: {
      itemsPerPage: params.pagination.count,
      pageNumber: params.pagination.page,
    },
    productType: ProductType.pharm,
    entryType: EntryType.registry,
    sortOrders: [],
    extendedQuery: params.extendedQuery || {},
  });

  return {
    totalCount: res.data.itemCount,
    data: res.data.itemList,
  };
};

const getPharmProducts = async (params: ChangeParams) => {
  const res = await baseApi.post('/pharm/list', {
    pageDetails: {
      itemsPerPage: params.pagination.count,
      pageNumber: params.pagination.page,
    },
    productType: ProductType.pharm,
    entryType: EntryType.registry,
    sortOrders: [
      {
        field: 'ph_startValidityDate',
        asc: false,
      },
    ],
    extendedQuery: {
      ...(params.extendedQuery as { name: string }),
    },
  });

  return {
    totalCount: res.data.total,
    data: res.data.result,
  };
};

const getMeasuringUnitsDict = async (
  code: MeasureUnitsCodes | null,
  type?: ProductType
): Promise<MeasureDictOption[]> => {
  const params: Record<string, boolean | number | string> = {
    count: 1000,
  };
  if (code) {
    params[code] = true;
  }
  if (type === ProductType.pharm) {
    params.isPharmaceutical = true;
  } else if (type === ProductType.substance) {
    params.isPharmSubstance = true;
  }
  const res = await baseApi.get('/catalogs/measuring-units', { params });
  return res.data.content;
};

const getConsignmentOriginsDict = async (
  type?: ProductType
): Promise<ConsignmentOriginDictOption[]> => {
  const res = await baseApi.get('/dictionary/consignmentOrigin', {
    params: { 'filter[registryProductType]': type },
  });
  return res.data;
};

const getLocalCountries = async (isExporter?: boolean): Promise<CountryDictOption[]> => {
  const res = await baseApi.post('/dictionary/localCountries', undefined, {
    params: { 'filter[isExporter]': isExporter },
  });
  return res.data.content;
};

const getProductNameAutocompleteList = async (
  params: FindFnParams<{ name?: string }>
): Promise<string[]> => {
  const res = await baseApi.post('/autocomplete/productNameAutocompleteList.json', params);
  return res.data;
};

const getVendorAutocompleteList = async (
  params: FindFnParams<{ vendor?: string }>
): Promise<string[]> => {
  const res = await baseApi.post('/autocomplete/vendorAutocompleteList.json', params);
  return res.data;
};

const getProducerAutocompleteList = async (
  params: FindFnParams<{ producer?: string }>
): Promise<string[]> => {
  const res = await baseApi.post('/autocomplete/producerAutocompleteList.json', params);
  return res.data;
};

const getApplicantAutocompleteList = async (
  params: FindFnParams<{ applicant?: string }>
): Promise<string[]> => {
  const res = await baseApi.post('/autocomplete/applicantAutocompleteList.json', params);
  return res.data;
};

const getObjectStatuses = async (params: ObjectStatusParams): Promise<ObjectStatus[]> => {
  const res = await baseApi.post('/object-statuses/find', {
    pageDetails: {
      itemsPerPage: 1000,
      pageNumber: 0,
    },
    sortOrders: [
      {
        field: 'os_name',
        asc: true,
      },
    ],
    extendedQuery: params,
  });
  return res.data.itemList;
};

const getObjectOperations = async (params: ObjectOperationParams): Promise<ObjectOperation[]> => {
  const res = await baseApi.post('/object-operations/find', {
    pageDetails: {
      itemsPerPage: 1000,
      pageNumber: 0,
    },
    extendedQuery: params,
  });
  return res.data.itemList;
};

const getGMPComplianceConclusion = async (params: ChangeParams) => {
  const res = await baseApi.post('/gmp-compliance-conclusions/find', {
    pageDetails: {
      itemsPerPage: params.pagination.count,
      pageNumber: params.pagination.page,
    },
    sortOrders: [],
    extendedQuery: params.extendedQuery || {},
  });

  return {
    totalCount: res.data.itemCount,
    data: res.data.itemList,
  };
};

const getOrgLegalForms = async (): Promise<OrgLegalFormDTO[]> => {
  const res = await baseApi.post('/org-legal-forms/find', {
    extendedQuery: {
      sectionCodeList: ['RU'],
    },
    pageDetails: {
      fullList: true,
    },
    sortOrders: [
      {
        field: 'olf_name',
        asc: true,
      },
    ],
  });

  return res.data.itemList;
};

const getNonproprietaryProductNames = async (
  nameRus?: AutocompleteValue
): Promise<BaseDictOption[]> => {
  const res = await baseApi.post('/catalogs/nonproprietary-product-names/find', {
    pageDetails: {
      itemsPerPage: 20,
      pageNumber: 0,
    },
    extendedQuery: {
      nameRus,
    },
  });
  return res.data.itemList;
};

export const dictApi = {
  baseDict,
  getRequestsStatus,
  getRegistryStatus,
  getCountries,
  getRussianRegions,
  getDistricts,
  getDocumentTypes,
  getConsiderationPeriodTypes,
  getAnimalTypes,
  getSigningPerson,
  getGMO,
  getStatuses,
  getRegistryOperations,
  getPharm,
  getPharmProducts,
  getPharmProductsList,
  getMeasuringUnitsDict,
  getConsignmentOriginsDict,
  getLocalCountries,
  getProductNameAutocompleteList,
  getVendorAutocompleteList,
  getProducerAutocompleteList,
  getApplicantAutocompleteList,
  getObjectStatuses,
  getObjectOperations,
  getGMPComplianceConclusion,
  getOrgLegalForms,
  getNonproprietaryProductNames,
};
